import React, { useState, useEffect } from 'react';
import { Plus, X } from 'lucide-react';

interface EmailRecipientsInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  description?: string;
}

const EmailRecipientsInput: React.FC<EmailRecipientsInputProps> = ({
  value,
  onChange,
  placeholder = "alerts@example.com",
  label = "Alert Emails",
  description = "Email addresses to receive alerts (comma-separated or add individually)"
}) => {
  const [emails, setEmails] = useState<string[]>([]);
  const [newEmail, setNewEmail] = useState('');

  // Sync internal list when parent value changes, but do not notify parent here
  useEffect(() => {
    const nextList = (value || '')
      .split(',')
      .map(e => e.trim())
      .filter(e => e);
    // Avoid unnecessary state updates
    const current = emails.join('\u0001');
    const next = nextList.join('\u0001');
    if (current !== next) {
      setEmails(nextList);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const addEmail = () => {
    if (newEmail.trim() && isValidEmail(newEmail.trim())) {
      if (!emails.includes(newEmail.trim())) {
        const updated = [...emails, newEmail.trim()];
        setEmails(updated);
        onChange(updated.join(', '));
        setNewEmail('');
      }
    }
  };

  const removeEmail = (index: number) => {
    const updated = emails.filter((_, i) => i !== index);
    setEmails(updated);
    onChange(updated.join(', '));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addEmail();
    }
  };

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    setNewEmail(input);
    
    // Auto-add emails when comma is typed
    if (input.includes(',')) {
      const parts = input.split(',');
      const validEmails = parts
        .slice(0, -1)
        .map(email => email.trim())
        .filter(email => email && isValidEmail(email));
      const remainingInput = parts[parts.length - 1].trim();
      
      // Add valid emails (dedup) in one state update
      if (validEmails.length > 0) {
        const set = new Set(emails);
        for (const email of validEmails) {
          set.add(email);
        }
        const updated = Array.from(set);
        setEmails(updated);
        onChange(updated.join(', '));
      }
      
      // Set remaining input
      setNewEmail(remainingInput);
    }
  };

  return (
    <div className="form-control">
      <label className="label">
        <span className="label-text font-medium">{label}</span>
        <span className="label-text-alt">{description}</span>
      </label>
      
      {/* Email list */}
      {emails.length > 0 && (
        <div className="mb-3 space-y-2">
          {emails.map((email, index) => (
            <div key={index} className="flex items-center gap-2 p-2 bg-base-200 rounded-lg">
              <span className="flex-1 text-sm">{email}</span>
              <button
                type="button"
                onClick={() => removeEmail(index)}
                className="btn btn-ghost btn-xs text-error hover:bg-error hover:text-error-content"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      
      {/* Add email input */}
      <div className="flex gap-2">
        <input
          type="email"
          className="input input-bordered flex-1"
          value={newEmail}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          placeholder={placeholder}
        />
        <button
          type="button"
          onClick={addEmail}
          disabled={!newEmail.trim() || !isValidEmail(newEmail.trim()) || emails.includes(newEmail.trim())}
          className="btn btn-primary btn-square"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>
      
      {/* Help text */}
      <div className="label">
        <span className="label-text-alt">
          Type email and press Enter or click + to add. Use commas to add multiple at once.
        </span>
      </div>
    </div>
  );
};

export default EmailRecipientsInput;
